import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../Context/LanguageContext';
import PageTransition from './PageTransition';

const reports = [
  {
    id: 'geri-donus-anketi-2026',
    slug: 'geri-donus-anketi-2026',
    folder: '/reports/geri-donus-anketi-2026',
    pdf: '/reports/geri-donus-anketi-2026/KTGPB-Geri-Donus-Anketi-2026.pdf',
    slideCount: 15,
    publishDate: '2026-04-01',
    surveyDateTr: '23 Ocak – 27 Şubat 2026',
    surveyDateEn: '23 January – 27 February 2026',
    participants: 145,
    titleTr: 'Kıbrıs\'a Geri Dönüş Anketi',
    titleEn: 'Return to Cyprus Survey',
    subtitleTr: 'Diaspora Genç Profesyonellerin Engelleri, Beklentileri ve Politika Önerileri',
    subtitleEn: 'Diaspora Young Professionals\' Barriers, Expectations and Policy Proposals',
    summaryTr: 'Yurt dışında yaşayan 145 genç Kıbrıslı Türk profesyonelin Kıbrıs\'a dönüş kararını etkileyen faktörler, kariyer beklentileri ve politika önerileri üzerine kapsamlı analiz.',
    summaryEn: 'A comprehensive analysis of 145 young Turkish Cypriot professionals abroad — covering factors influencing their decision to return, career expectations and policy proposals.',
    keyFindings: [
      {
        metric: '%42',
        labelTr: 'Fırsat',
        labelEn: 'Opportunity',
        descTr: 'Katılımcıların %42\'si dönüş konusunda koşullu veya kararsız — ikna edilebilir bir havuz.',
        descEn: '42% of participants are conditional or undecided about returning — a persuadable pool.'
      },
      {
        metric: '4.28/5',
        labelTr: '1 Numaralı Engel',
        labelEn: 'Top Barrier',
        descTr: 'Kariyer kaygıları (liyakatsizlik, düşük ücret, sınırlı fırsat) tüm segmentlerde baskın engel.',
        descEn: 'Career concerns (lack of meritocracy, low pay, limited opportunity) dominate across all segments.'
      },
      {
        metric: '%81',
        labelTr: 'Kırılma Noktası',
        labelEn: 'Breaking Point',
        descTr: 'Katılımcıların %81\'i dönse bile tekrar yurt dışına gitme endişesi taşıyor.',
        descEn: '81% of participants worry about going abroad again, even if they return.'
      },
      {
        metric: '1.66/5',
        labelTr: 'Acil Eylem',
        labelEn: 'Urgent Action',
        descTr: 'Teşvik ve desteklere bilgi erişimi 5 üzerinden 1.66 — devlet ile diyalog çok zayıf.',
        descEn: 'Access to information about incentives is just 1.66/5 — dialogue with the state is very weak.'
      }
    ]
  }
];

const Raporlar = () => {
  const { language, t } = useLanguage();
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerRef = useRef(null);
  const thumbStripRef = useRef(null);
  const stageRef = useRef(null);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const goToSlide = useCallback((n) => {
    if (!selectedReport) return;
    const clamped = Math.max(1, Math.min(selectedReport.slideCount, n));
    setCurrentSlide(clamped);
  }, [selectedReport]);

  const nextSlide = useCallback(() => {
    if (!selectedReport) return;
    if (currentSlide < selectedReport.slideCount) goToSlide(currentSlide + 1);
  }, [currentSlide, selectedReport, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 1) goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const openReport = (report) => {
    setSelectedReport(report);
    setCurrentSlide(1);
  };

  const closeReport = () => {
    if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    }
    setSelectedReport(null);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    if (!viewerRef.current) return;
    if (!document.fullscreenElement) {
      viewerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!selectedReport) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape' && !document.fullscreenElement) {
        closeReport();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToSlide(1);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToSlide(selectedReport.slideCount);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, currentSlide, nextSlide, prevSlide, goToSlide]);

  // Auto-scroll the active thumbnail into view
  useEffect(() => {
    if (!thumbStripRef.current) return;
    const activeThumb = thumbStripRef.current.querySelector('.report-thumb.active');
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentSlide]);

  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) nextSlide();
      else prevSlide();
    }
    touchStartX.current = null;
  };

  return (
    <PageTransition>
      <section id="raporlar" className="reports-section">
        <AnimatePresence mode="wait">
          {!selectedReport ? (
            // ================ LIST VIEW ================
            <motion.div
              key="list"
              className="reports-list-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="reports-hero">
                <h1>{t('reports.title')}</h1>
                <p>{t('reports.subtitle')}</p>
              </div>

              <div className="reports-grid">
                {reports.length === 0 ? (
                  <div className="no-results">
                    <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                    <p>{t('reports.noReports')}</p>
                  </div>
                ) : (
                  reports.map((report) => (
                    <motion.article
                      key={report.id}
                      className="report-card"
                      onClick={() => openReport(report)}
                      whileHover={{ y: -4 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                    >
                      <div className="report-card-cover">
                        <img
                          src={`${report.folder}/Slide1.png`}
                          alt={language === 'tr' ? report.titleTr : report.titleEn}
                          loading="lazy"
                        />
                        <div className="report-card-overlay">
                          <span className="report-card-cta">
                            <i className="fa fa-play-circle" aria-hidden="true"></i>
                            {t('reports.view')}
                          </span>
                        </div>
                      </div>
                      <div className="report-card-body">
                        <div className="report-card-meta">
                          <span><i className="fa fa-calendar" aria-hidden="true"></i> {formatDate(report.publishDate)}</span>
                          <span><i className="fa fa-users" aria-hidden="true"></i> {report.participants} {t('reports.participants')}</span>
                          <span><i className="fa fa-file-image-o" aria-hidden="true"></i> {report.slideCount} {t('reports.slides')}</span>
                        </div>
                        <h2 className="report-card-title">
                          {language === 'tr' ? report.titleTr : report.titleEn}
                        </h2>
                        <p className="report-card-subtitle">
                          {language === 'tr' ? report.subtitleTr : report.subtitleEn}
                        </p>
                        <p className="report-card-summary">
                          {language === 'tr' ? report.summaryTr : report.summaryEn}
                        </p>
                        <div className="report-card-actions">
                          <button
                            className="btn-primary"
                            onClick={(e) => { e.stopPropagation(); openReport(report); }}
                          >
                            <i className="fa fa-eye" aria-hidden="true"></i> {t('reports.view')}
                          </button>
                          <a
                            className="btn-secondary"
                            href={report.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fa fa-download" aria-hidden="true"></i> {t('reports.downloadPdf')}
                          </a>
                        </div>
                      </div>
                    </motion.article>
                  ))
                )}
              </div>
            </motion.div>
          ) : (
            // ================ VIEWER ================
            <motion.div
              key="viewer"
              className={`report-viewer ${isFullscreen ? 'is-fullscreen' : ''}`}
              ref={viewerRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="report-viewer-header">
                <button className="viewer-back" onClick={closeReport}>
                  <i className="fa fa-arrow-left" aria-hidden="true"></i>
                  <span>{t('reports.backToList')}</span>
                </button>
                <div className="viewer-titleblock">
                  <h2>{language === 'tr' ? selectedReport.titleTr : selectedReport.titleEn}</h2>
                  <p>{language === 'tr' ? selectedReport.subtitleTr : selectedReport.subtitleEn}</p>
                </div>
                <div className="viewer-actions">
                  <a
                    href={selectedReport.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="viewer-action-btn"
                    title={t('reports.downloadPdf')}
                  >
                    <i className="fa fa-download" aria-hidden="true"></i>
                    <span className="action-label">{t('reports.downloadPdf')}</span>
                  </a>
                  <button
                    className="viewer-action-btn"
                    onClick={toggleFullscreen}
                    title={isFullscreen ? t('reports.exitFullscreen') : t('reports.fullscreen')}
                  >
                    <i className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true"></i>
                    <span className="action-label">
                      {isFullscreen ? t('reports.exitFullscreen') : t('reports.fullscreen')}
                    </span>
                  </button>
                </div>
              </div>

              <div
                className="report-stage"
                ref={stageRef}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                <button
                  className="stage-nav stage-nav-prev"
                  onClick={prevSlide}
                  disabled={currentSlide === 1}
                  aria-label={t('reports.previous')}
                >
                  <i className="fa fa-chevron-left" aria-hidden="true"></i>
                </button>

                <img
                  src={`${selectedReport.folder}/Slide${currentSlide}.png`}
                  alt={`${t('reports.slide')} ${currentSlide}`}
                  className="stage-slide"
                  draggable={false}
                />

                {/* Hidden preloaders for neighbouring slides so swap is instant */}
                {currentSlide < selectedReport.slideCount && (
                  <img
                    src={`${selectedReport.folder}/Slide${currentSlide + 1}.png`}
                    alt=""
                    aria-hidden="true"
                    className="stage-preload"
                  />
                )}
                {currentSlide > 1 && (
                  <img
                    src={`${selectedReport.folder}/Slide${currentSlide - 1}.png`}
                    alt=""
                    aria-hidden="true"
                    className="stage-preload"
                  />
                )}

                <button
                  className="stage-nav stage-nav-next"
                  onClick={nextSlide}
                  disabled={currentSlide === selectedReport.slideCount}
                  aria-label={t('reports.next')}
                >
                  <i className="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
              </div>

              <div className="report-controls">
                <div className="slide-counter">
                  <span className="slide-num">{currentSlide}</span>
                  <span className="slide-sep">{t('reports.of')}</span>
                  <span className="slide-total">{selectedReport.slideCount}</span>
                </div>
                <div className="slide-progress">
                  <div
                    className="slide-progress-bar"
                    style={{ width: `${(currentSlide / selectedReport.slideCount) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="report-thumbs" ref={thumbStripRef}>
                {Array.from({ length: selectedReport.slideCount }).map((_, idx) => {
                  const n = idx + 1;
                  return (
                    <button
                      key={n}
                      className={`report-thumb ${n === currentSlide ? 'active' : ''}`}
                      onClick={() => goToSlide(n)}
                      aria-label={`${t('reports.slide')} ${n}`}
                    >
                      <img
                        src={`${selectedReport.folder}/Slide${n}.png`}
                        alt=""
                        loading="lazy"
                        draggable={false}
                      />
                      <span className="thumb-num">{n}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </PageTransition>
  );
};

export default Raporlar;
